'use client';

import React, { useState, useMemo, useEffect, useRef } from 'react';
import AppLayout from '@/components/AppLayout';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Package,
  Users,
  Building2,
  Phone,
  Mail,
  Globe,
  MapPin,
  Star,
  ChevronDown,
  ChevronUp,
  ArrowUpRight,
  ArrowDownLeft,
  Truck,
  ShoppingBag,
  Leaf,
  Gem,
  Zap,
  Fish,
  Pencil,
  Check,
  X,
  Plus,
  Trash2,
  Search,
} from 'lucide-react';

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface ThaiSupplier {
  id: string;
  name: string;
  category: string;
  products: string[];
  address: string;
  phone: string;
  email: string;
  website: string;
  certifications: string[];
  minOrder: string;
  leadTime: string;
  rating: number;
}

// ─── DATA ────────────────────────────────────────────────────────────────────

const indiaToThailandExports = [
  { category: 'Pharmaceuticals', products: ['Generic Medicines', 'API Bulk Drugs', 'Ayurvedic Formulations', 'Vaccines'], value: 1240, unit: 'M USD', yoy: 18.4, icon: '💊', color: '#4F6EF7' },
  { category: 'Gems & Jewellery', products: ['Cut Diamonds', 'Gold Jewellery', 'Silver Ornaments', 'Precious Stones'], value: 980, unit: 'M USD', yoy: 12.1, icon: '💎', color: '#8B5CF6' },
  { category: 'Machinery & Equipment', products: ['Textile Machinery', 'Pumps & Compressors', 'Industrial Boilers', 'CNC Machines'], value: 870, unit: 'M USD', yoy: 9.7, icon: '⚙️', color: '#0EA5E9' },
  { category: 'Organic Chemicals', products: ['Dyes & Pigments', 'Pesticide Intermediates', 'Specialty Chemicals', 'Solvents'], value: 760, unit: 'M USD', yoy: 14.2, icon: '🧪', color: '#10B981' },
  { category: 'Spices & Condiments', products: ['Black Pepper', 'Turmeric', 'Cardamom', 'Chilli Powder', 'Cumin'], value: 640, unit: 'M USD', yoy: 22.5, icon: '🌶️', color: '#F59E0B' },
  { category: 'Textiles & Apparel', products: ['Cotton Yarn', 'Woven Fabrics', 'Ready-made Garments', 'Silk Sarees'], value: 580, unit: 'M USD', yoy: 7.3, icon: '👗', color: '#EC4899' },
  { category: 'Rice & Cereals', products: ['Basmati Rice', 'Non-Basmati Rice', 'Wheat Flour', 'Maize'], value: 520, unit: 'M USD', yoy: 31.0, icon: '🌾', color: '#84CC16' },
  { category: 'Auto Components', products: ['Brake Systems', 'Engine Parts', 'Electrical Components', 'Forged Parts'], value: 430, unit: 'M USD', yoy: 5.8, icon: '🔧', color: '#F97316' },
  { category: 'Leather Products', products: ['Leather Footwear', 'Handbags', 'Leather Garments', 'Belts & Wallets'], value: 310, unit: 'M USD', yoy: -2.1, icon: '👜', color: '#A78BFA' },
  { category: 'Processed Foods', products: ['Mango Pulp', 'Pickles & Chutneys', 'Biscuits & Snacks', 'Dairy Products'], value: 290, unit: 'M USD', yoy: 19.6, icon: '🥫', color: '#34D399' },
];

const thailandToIndiaImports = [
  { category: 'Electronic Components', products: ['Integrated Circuits', 'Printed Circuit Boards', 'Capacitors', 'Semiconductors'], value: 2100, unit: 'M USD', yoy: 24.3, icon: '🔌', color: '#4F6EF7' },
  { category: 'Rubber & Rubber Products', products: ['Natural Rubber', 'Rubber Gloves', 'Rubber Sheets', 'Latex Concentrate'], value: 1450, unit: 'M USD', yoy: 11.8, icon: '🏭', color: '#F59E0B' },
  { category: 'Plastic Materials', products: ['PVC Granules', 'Polypropylene', 'HDPE Sheets', 'Plastic Pipes'], value: 980, unit: 'M USD', yoy: 8.5, icon: '🧴', color: '#0EA5E9' },
  { category: 'Machinery (Industrial)', products: ['CNC Lathes', 'Hydraulic Presses', 'Conveyor Systems', 'Packaging Machines'], value: 870, unit: 'M USD', yoy: 16.2, icon: '🏗️', color: '#10B981' },
  { category: 'Tropical Fruits', products: ['Durian', 'Mangosteen', 'Longan', 'Rambutan', 'Lychee'], value: 620, unit: 'M USD', yoy: 28.7, icon: '🍍', color: '#84CC16' },
  { category: 'Seafood & Fish Products', products: ['Canned Tuna', 'Frozen Shrimp', 'Fish Sauce', 'Dried Squid'], value: 540, unit: 'M USD', yoy: 13.4, icon: '🐟', color: '#06B6D4' },
  { category: 'Chemical Products', products: ['Petrochemicals', 'Adhesives', 'Coating Materials', 'Industrial Solvents'], value: 490, unit: 'M USD', yoy: 6.9, icon: '⚗️', color: '#8B5CF6' },
  { category: 'Automotive Parts', products: ['Gear Assemblies', 'Brake Pads', 'Suspension Parts', 'Wiring Harness'], value: 420, unit: 'M USD', yoy: 9.1, icon: '🚗', color: '#F97316' },
  { category: 'Processed Tapioca', products: ['Tapioca Starch', 'Tapioca Chips', 'Modified Starch', 'Cassava Flour'], value: 310, unit: 'M USD', yoy: 4.2, icon: '🌿', color: '#A3E635' },
  { category: 'Cosmetics & Toiletries', products: ['Skin Whitening Creams', 'Herbal Shampoos', 'Sunscreen Lotions', 'Lip Care'], value: 270, unit: 'M USD', yoy: 21.5, icon: '💄', color: '#EC4899' },
];

const indianRestaurantsBangkok = [
  {
    name: 'Rang Mahal',
    type: 'Fine Dining — North Indian',
    address: 'Rembrandt Hotel, Sukhumvit Soi 18, Bangkok 10110',
    phone: '+66 2 261 7100',
    email: 'rangmahal@rembrandtbkk.com',
    website: 'www.rembrandtbkk.com',
    rating: 4.7,
    established: 1992,
    monthlyProcurement: '~฿180,000',
    keyImports: ['Basmati Rice', 'Spices', 'Paneer', 'Ghee'],
    buyerStatus: 'Active',
  },
  {
    name: 'Indus Restaurant',
    type: 'Casual Fine — Pan-Indian',
    address: '71 Sukhumvit Soi 26, Khlong Toei, Bangkok 10110',
    phone: '+66 2 258 4900',
    email: 'info@indusbangkok.com',
    website: 'www.indusbangkok.com',
    rating: 4.5,
    established: 2003,
    monthlyProcurement: '~฿140,000',
    keyImports: ['Lentils', 'Spice Blends', 'Pickles', 'Papadum'],
    buyerStatus: 'Active',
  },
  {
    name: 'Gaggan Anand',
    type: 'Progressive Indian — Michelin Starred',
    address: '68/1 Soi Langsuan, Ploenchit Rd, Bangkok 10330',
    phone: '+66 2 652 1700',
    email: 'reservations@eatatgaggan.com',
    website: 'www.eatatgaggan.com',
    rating: 4.9,
    established: 2010,
    monthlyProcurement: '~฿320,000',
    keyImports: ['Specialty Spices', 'Organic Pulses', 'Artisan Ghee', 'Heirloom Rice'],
    buyerStatus: 'Active',
  },
  {
    name: 'Baan Khanitha & Gallery',
    type: 'Indian-Thai Fusion',
    address: '36/1 Sukhumvit Soi 23, Wattana, Bangkok 10110',
    phone: '+66 2 258 4181',
    email: 'contact@baankhanitha.com',
    website: 'www.baankhanitha.com',
    rating: 4.3,
    established: 1998,
    monthlyProcurement: '~฿90,000',
    keyImports: ['Curry Pastes', 'Dried Spices', 'Basmati Rice'],
    buyerStatus: 'Active',
  },
  {
    name: 'Himali Cha Cha',
    type: 'Casual — North Indian & Nepali',
    address: '1229/11 Charoen Krung Rd, Bang Rak, Bangkok 10500',
    phone: '+66 2 235 1569',
    email: 'hcc@himalirestaurant.com',
    website: 'www.himalirestaurant.com',
    rating: 4.4,
    established: 1976,
    monthlyProcurement: '~฿110,000',
    keyImports: ['Whole Spices', 'Atta Flour', 'Ghee', 'Dried Fruits'],
    buyerStatus: 'Active',
  },
  {
    name: 'Spice & Rice',
    type: 'Casual — South Indian',
    address: '4/2 Silom Soi 3, Bang Rak, Bangkok 10500',
    phone: '+66 2 234 7171',
    email: 'info@spiceandrice.co.th',
    website: 'www.spiceandrice.co.th',
    rating: 4.2,
    established: 2008,
    monthlyProcurement: '~฿75,000',
    keyImports: ['Idli Rice', 'Sambar Powder', 'Coconut Oil', 'Tamarind'],
    buyerStatus: 'Active',
  },
  {
    name: 'Maharaja Indian Restaurant',
    type: 'Casual — Mughlai & Punjabi',
    address: '3rd Floor, Nana Plaza, Sukhumvit Soi 4, Bangkok',
    phone: '+66 2 252 9860',
    email: 'maharajabkk@gmail.com',
    website: 'www.maharajabangkok.com',
    rating: 4.1,
    established: 2001,
    monthlyProcurement: '~฿95,000',
    keyImports: ['Tandoor Spices', 'Basmati', 'Paneer', 'Chutney'],
    buyerStatus: 'Active',
  },
  {
    name: 'Curry Pot',
    type: 'Quick Service — Indian Street Food',
    address: '22 Sukhumvit Soi 11, Khlong Toei Nuea, Bangkok',
    phone: '+66 2 651 3456',
    email: 'currypotbkk@outlook.com',
    website: 'www.currypotbangkok.com',
    rating: 4.0,
    established: 2015,
    monthlyProcurement: '~฿55,000',
    keyImports: ['Ready Spice Mixes', 'Canned Goods', 'Papadum'],
    buyerStatus: 'Active',
  },
];

const initialThaiSuppliers: ThaiSupplier[] = [
  {
    id: 's1',
    name: 'Thai Union Group PCL',
    category: 'Seafood & Fish Products',
    products: ['Canned Tuna', 'Frozen Shrimp', 'Processed Seafood', 'Fish Sauce'],
    address: '72/1 Moo 7, Sethakit 1 Rd, Samut Sakhon 74000',
    phone: '+66 34 417 222',
    email: 'export@thaiunion.com',
    website: 'www.thaiunion.com',
    certifications: ['ISO 22000', 'BRC', 'MSC', 'ASC'],
    minOrder: 'USD 10,000',
    leadTime: '30–45 days',
    rating: 4.8,
  },
  {
    id: 's2',
    name: 'Hana Microelectronics',
    category: 'Electronic Components',
    products: ['PCBs', 'Semiconductor Assemblies', 'Electronic Modules', 'LED Components'],
    address: '605 Moo 5, Bangpoo Industrial Estate, Samut Prakan 10280',
    phone: '+66 2 709 9000',
    email: 'sales@hana.com',
    website: 'www.hana.com',
    certifications: ['ISO 9001', 'ISO 14001', 'IATF 16949'],
    minOrder: 'USD 25,000',
    leadTime: '45–60 days',
    rating: 4.6,
  },
  {
    id: 's3',
    name: 'Sri Trang Agro-Industry',
    category: 'Rubber Products',
    products: ['Natural Rubber', 'Latex Concentrate', 'Rubber Gloves', 'Rubber Sheets'],
    address: '10 Soi 10, Phetkasem Rd, Hat Yai, Songkhla 90110',
    phone: '+66 74 471 491',
    email: 'export@sritrang.com',
    website: 'www.sritrang.com',
    certifications: ['ISO 9001', 'FSC', 'RSPO'],
    minOrder: 'USD 15,000',
    leadTime: '21–30 days',
    rating: 4.7,
  },
  {
    id: 's4',
    name: 'Charoen Pokphand Foods (CPF)',
    category: 'Tropical Fruits & Processed Foods',
    products: ['Frozen Durian', 'Mango Pulp', 'Pineapple Products', 'Tapioca Starch'],
    address: 'CP Tower, 313 Silom Rd, Bangrak, Bangkok 10500',
    phone: '+66 2 638 2000',
    email: 'international@cpf.co.th',
    website: 'www.cpfworldwide.com',
    certifications: ['ISO 22000', 'HACCP', 'GlobalGAP'],
    minOrder: 'USD 20,000',
    leadTime: '28–35 days',
    rating: 4.5,
  },
  {
    id: 's5',
    name: 'PTT Global Chemical',
    category: 'Chemical & Plastic Materials',
    products: ['Polyethylene', 'Polypropylene', 'Specialty Chemicals', 'Petrochemicals'],
    address: '555/1 Energy Complex Bldg A, Vibhavadi Rangsit Rd, Bangkok 10900',
    phone: '+66 2 265 8400',
    email: 'export@pttgc.com',
    website: 'www.pttgcgroup.com',
    certifications: ['ISO 9001', 'ISO 14001', 'OHSAS 18001'],
    minOrder: 'USD 50,000',
    leadTime: '30–45 days',
    rating: 4.4,
  },
  {
    id: 's6',
    name: 'Mitr Phol Sugar Corp',
    category: 'Processed Tapioca & Sugar',
    products: ['Tapioca Chips', 'Tapioca Starch', 'Refined Sugar', 'Molasses'],
    address: '2 Mitr Phol Tower, Ratchadaphisek Rd, Bangkok 10310',
    phone: '+66 2 694 2000',
    email: 'trade@mitrphol.com',
    website: 'www.mitrphol.com',
    certifications: ['ISO 9001', 'Bonsucro', 'FSSC 22000'],
    minOrder: 'USD 12,000',
    leadTime: '21–28 days',
    rating: 4.3,
  },
];

const exportChartData = indiaToThailandExports.slice(0, 7).map((d) => ({
  name: d.category.split(' ')[0],
  value: d.value,
  fill: d.color,
}));

const importChartData = thailandToIndiaImports.slice(0, 7).map((d) => ({
  name: d.category.split(' ')[0],
  value: d.value,
  fill: d.color,
}));

const tradePieData = [
  { name: 'India → Thailand', value: 7620, color: '#4F6EF7' },
  { name: 'Thailand → India', value: 8050, color: '#F59E0B' },
];

// ─── SEARCH TYPES ─────────────────────────────────────────────────────────────

interface SearchResult {
  type: 'supplier' | 'buyer' | 'export' | 'import';
  label: string;
  sub: string;
  tab: TabId;
}

function buildSearchIndex(suppliers: ThaiSupplier[]): SearchResult[] {
  const results: SearchResult[] = [];

  suppliers.forEach((s) => {
    results.push({
      type: 'supplier',
      label: s.name,
      sub: s.category + (s.products.length ? ' · ' + s.products.slice(0, 2).join(', ') : ''),
      tab: 'suppliers',
    });
  });

  indianRestaurantsBangkok.forEach((b) => {
    results.push({
      type: 'buyer',
      label: b.name,
      sub: b.type + ' · ' + b.phone,
      tab: 'buyers',
    });
  });

  indiaToThailandExports.forEach((e) => {
    results.push({
      type: 'export',
      label: e.category,
      sub: `India → Bangkok · $${e.value}M · ${e.yoy >= 0 ? '+' : ''}${e.yoy}% YoY`,
      tab: 'exports',
    });
  });

  thailandToIndiaImports.forEach((i) => {
    results.push({
      type: 'import',
      label: i.category,
      sub: `Bangkok → India · $${i.value}M · ${i.yoy >= 0 ? '+' : ''}${i.yoy}% YoY`,
      tab: 'imports',
    });
  });

  return results;
}

const TYPE_LABELS: Record<SearchResult['type'], string> = {
  supplier: 'Supplier',
  buyer: 'Buyer',
  export: 'Export Product',
  import: 'Import Product',
};

const TYPE_COLORS: Record<SearchResult['type'], string> = {
  supplier: 'bg-green-50 text-green-700 border-green-200',
  buyer: 'bg-purple-50 text-purple-700 border-purple-200',
  export: 'bg-blue-50 text-blue-700 border-blue-200',
  import: 'bg-amber-50 text-amber-700 border-amber-200',
};

// ─── UNIFIED SEARCH BAR ───────────────────────────────────────────────────────

function UnifiedSearchBar({
  suppliers,
  onNavigate,
}: {
  suppliers: ThaiSupplier[];
  onNavigate: (tab: TabId, query: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const index = React.useMemo(() => buildSearchIndex(suppliers), [suppliers]);

  const results = React.useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return index.filter(
      (r) =>
        r.label.toLowerCase().includes(q) ||
        r.sub.toLowerCase().includes(q)
    ).slice(0, 12);
  }, [query, index]);

  React.useEffect(() => {
    setOpen(focused && query.trim().length > 0);
  }, [focused, query]);

  React.useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setFocused(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = (result: SearchResult) => {
    onNavigate(result.tab, query);
    setQuery('');
    setOpen(false);
    setFocused(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
      inputRef.current?.blur();
    }
  };

  const grouped = React.useMemo(() => {
    const map: Partial<Record<SearchResult['type'], SearchResult[]>> = {};
    results.forEach((r) => {
      if (!map[r.type]) map[r.type] = [];
      map[r.type]!.push(r);
    });
    return map;
  }, [results]);

  return (
    <div ref={containerRef} className="relative w-full max-w-xl">
      <div
        className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border bg-card transition-all duration-150 ${
          focused ? 'border-primary/50 shadow-sm ring-2 ring-primary/10' : 'border-border'
        }`}
      >
        <Search size={15} className={`shrink-0 transition-colors ${focused ? 'text-primary' : 'text-muted-foreground'}`} />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search suppliers, buyers, products, contacts…"
          className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none min-w-0"
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setOpen(false); inputRef.current?.focus(); }}
            className="shrink-0 h-5 w-5 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
          >
            <X size={11} />
          </button>
        )}
        {!query && (
          <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-border text-[10px] font-mono text-muted-foreground bg-muted shrink-0">
            /
          </kbd>
        )}
      </div>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden max-h-80 overflow-y-auto">
          {results.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <p className="text-sm text-muted-foreground">No results for <span className="font-semibold text-foreground">"{query}"</span></p>
            </div>
          ) : (
            <div className="py-1.5">
              {(Object.keys(grouped) as SearchResult['type'][]).map((type) => (
                <div key={type}>
                  <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground bg-muted/40">
                    {TYPE_LABELS[type]}s
                  </div>
                  {grouped[type]!.map((result, i) => (
                    <button
                      key={i}
                      onClick={() => handleSelect(result)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/60 transition-colors text-left"
                    >
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border shrink-0 ${TYPE_COLORS[type]}`}>
                        {TYPE_LABELS[type]}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate">{result.label}</p>
                        <p className="text-[11px] text-muted-foreground truncate">{result.sub}</p>
                      </div>
                      <ArrowUpRight size={12} className="text-muted-foreground shrink-0" />
                    </button>
                  ))}
                </div>
              ))}
              <div className="px-3 py-2 border-t border-border">
                <p className="text-[10px] text-muted-foreground text-center">
                  {results.length} result{results.length !== 1 ? 's' : ''} · Press <kbd className="px-1 py-0.5 rounded border border-border font-mono bg-muted">Esc</kbd> to close
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── SUB-COMPONENTS ──────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  sub,
  trend,
  color,
  icon,
}: {
  label: string;
  value: string;
  sub: string;
  trend?: number;
  color: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="bg-card rounded-xl border border-border p-5 flex flex-col gap-3 shadow-card card-hover">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{label}</span>
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center`} style={{ background: color + '18' }}>
          <span style={{ color }}>{icon}</span>
        </div>
      </div>
      <div>
        <p className="text-2xl font-bold text-foreground font-tabular">{value}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 text-xs font-semibold ${trend >= 0 ? 'text-green-600' : 'text-red-500'}`}>
          {trend >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
          {trend >= 0 ? '+' : ''}{trend}% YoY
        </div>
      )}
    </div>
  );
}

function ProductRow({ item, rank }: { item: typeof indiaToThailandExports[0]; rank: number }) {
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-border last:border-0">
      <span className="text-xs font-bold text-muted-foreground w-5 text-right shrink-0">{rank}</span>
      <span className="text-lg shrink-0">{item.icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground truncate">{item.category}</p>
        <p className="text-[11px] text-muted-foreground truncate mt-0.5">{item.products.slice(0, 3).join(' · ')}</p>
        <div className="mt-1 h-1.5 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: `${(item.value / indiaToThailandExports[0].value) * 100}%`,
              background: item.color,
            }}
          />
        </div>
      </div>
      <div className="text-right shrink-0">
        <p className="text-sm font-bold text-foreground font-tabular">${item.value}M</p>
        <p className={`text-[11px] font-semibold ${item.yoy >= 0 ? 'text-green-600' : 'text-red-500'}`}>
          {item.yoy >= 0 ? '+' : ''}{item.yoy}%
        </p>
      </div>
    </div>
  );
}

function ImportRow({ item, rank }: { item: typeof thailandToIndiaImports[0]; rank: number }) {
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-border last:border-0">
      <span className="text-xs font-bold text-muted-foreground w-5 text-right shrink-0">{rank}</span>
      <span className="text-lg shrink-0">{item.icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground truncate">{item.category}</p>
        <p className="text-[11px] text-muted-foreground truncate mt-0.5">{item.products.slice(0, 3).join(' · ')}</p>
        <div className="mt-1 h-1.5 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: `${(item.value / thailandToIndiaImports[0].value) * 100}%`,
              background: item.color,
            }}
          />
        </div>
      </div>
      <div className="text-right shrink-0">
        <p className="text-sm font-bold text-foreground font-tabular">${item.value}M</p>
        <p className={`text-[11px] font-semibold ${item.yoy >= 0 ? 'text-green-600' : 'text-red-500'}`}>
          {item.yoy >= 0 ? '+' : ''}{item.yoy}%
        </p>
      </div>
    </div>
  );
}

function BuyerCard({ buyer }: { buyer: typeof indianRestaurantsBangkok[0] }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bg-card rounded-xl border border-border shadow-card overflow-hidden card-hover">
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-bold text-foreground">{buyer.name}</h3>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-50 text-green-700 border border-green-200">
                {buyer.buyerStatus}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{buyer.type}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Star size={12} className="text-amber-400 fill-amber-400" />
            <span className="text-xs font-bold text-foreground">{buyer.rating}</span>
          </div>
        </div>

        <div className="mt-3 space-y-1.5">
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <MapPin size={12} className="shrink-0 mt-0.5" />
            <span className="leading-tight">{buyer.address}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Phone size={12} className="shrink-0" />
            <a href={`tel:${buyer.phone}`} className="hover:text-primary transition-colors">{buyer.phone}</a>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Mail size={12} className="shrink-0" />
            <a href={`mailto:${buyer.email}`} className="hover:text-primary transition-colors truncate">{buyer.email}</a>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-1">
          {buyer.keyImports.map((k) => (
            <span key={k} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary/8 text-primary border border-primary/20">
              {k}
            </span>
          ))}
        </div>
      </div>

      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between px-4 py-2 bg-muted/50 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors border-t border-border"
      >
        <span>Est. {buyer.established} · Monthly procurement {buyer.monthlyProcurement}</span>
        {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
      </button>

      {expanded && (
        <div className="px-4 py-3 bg-muted/30 border-t border-border">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Globe size={12} className="shrink-0" />
            <a href={`https://${buyer.website}`} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
              {buyer.website}
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── EDITABLE SUPPLIER CARD ───────────────────────────────────────────────────

function EditableSupplierCard({
  supplier,
  onSave,
  onDelete,
}: {
  supplier: ThaiSupplier;
  onSave: (updated: ThaiSupplier) => void;
  onDelete: (id: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ThaiSupplier>(supplier);
  const [productsInput, setProductsInput] = useState(supplier.products.join(', '));
  const [certsInput, setCertsInput] = useState(supplier.certifications.join(', '));

  const handleEdit = () => {
    setDraft(supplier);
    setProductsInput(supplier.products.join(', '));
    setCertsInput(supplier.certifications.join(', '));
    setEditing(true);
  };

  const handleSave = () => {
    const updated: ThaiSupplier = {
      ...draft,
      products: productsInput.split(',').map((s) => s.trim()).filter(Boolean),
      certifications: certsInput.split(',').map((s) => s.trim()).filter(Boolean),
    };
    onSave(updated);
    setEditing(false);
  };

  const handleCancel = () => {
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="bg-card rounded-xl border-2 border-primary/40 shadow-card p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-bold text-primary uppercase tracking-wide">Editing Supplier</span>
          <div className="flex items-center gap-1.5">
            <button
              onClick={handleSave}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-green-600 text-white text-xs font-semibold hover:bg-green-700 transition-colors"
            >
              <Check size={12} /> Save
            </button>
            <button
              onClick={handleCancel}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-muted text-muted-foreground text-xs font-semibold hover:text-foreground transition-colors border border-border"
            >
              <X size={12} /> Cancel
            </button>
          </div>
        </div>

        <div className="space-y-2.5">
          <div>
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Company Name</label>
            <input
              className="w-full text-sm border border-border rounded-lg px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Category</label>
            <input
              className="w-full text-sm border border-border rounded-lg px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              value={draft.category}
              onChange={(e) => setDraft({ ...draft, category: e.target.value })}
            />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Products (comma-separated)</label>
            <input
              className="w-full text-sm border border-border rounded-lg px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              value={productsInput}
              onChange={(e) => setProductsInput(e.target.value)}
            />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Address</label>
            <input
              className="w-full text-sm border border-border rounded-lg px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              value={draft.address}
              onChange={(e) => setDraft({ ...draft, address: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Phone</label>
              <input
                className="w-full text-sm border border-border rounded-lg px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                value={draft.phone}
                onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Rating (0–5)</label>
              <input
                type="number"
                min={0}
                max={5}
                step={0.1}
                className="w-full text-sm border border-border rounded-lg px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                value={draft.rating}
                onChange={(e) => setDraft({ ...draft, rating: parseFloat(e.target.value) || 0 })}
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Email</label>
            <input
              className="w-full text-sm border border-border rounded-lg px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              value={draft.email}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })}
            />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Website</label>
            <input
              className="w-full text-sm border border-border rounded-lg px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              value={draft.website}
              onChange={(e) => setDraft({ ...draft, website: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Min. Order</label>
              <input
                className="w-full text-sm border border-border rounded-lg px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                value={draft.minOrder}
                onChange={(e) => setDraft({ ...draft, minOrder: e.target.value })}
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Lead Time</label>
              <input
                className="w-full text-sm border border-border rounded-lg px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                value={draft.leadTime}
                onChange={(e) => setDraft({ ...draft, leadTime: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Certifications (comma-separated)</label>
            <input
              className="w-full text-sm border border-border rounded-lg px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              value={certsInput}
              onChange={(e) => setCertsInput(e.target.value)}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border shadow-card p-4 card-hover group relative">
      {/* Action buttons */}
      <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={handleEdit}
          title="Edit supplier"
          className="h-7 w-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center hover:bg-primary/20 transition-colors"
        >
          <Pencil size={12} />
        </button>
        <button
          onClick={() => onDelete(supplier.id)}
          title="Delete supplier"
          className="h-7 w-7 rounded-lg bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 transition-colors"
        >
          <Trash2 size={12} />
        </button>
      </div>

      <div className="flex items-start justify-between gap-2 mb-3 pr-16">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-foreground leading-tight">{supplier.name}</h3>
          <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
            {supplier.category}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Star size={12} className="text-amber-400 fill-amber-400" />
          <span className="text-xs font-bold text-foreground">{supplier.rating}</span>
        </div>
      </div>

      <div className="space-y-1.5 mb-3">
        <div className="flex items-start gap-2 text-xs text-muted-foreground">
          <MapPin size={12} className="shrink-0 mt-0.5" />
          <span className="leading-tight">{supplier.address}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Phone size={12} className="shrink-0" />
          <a href={`tel:${supplier.phone}`} className="hover:text-primary transition-colors">{supplier.phone}</a>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Mail size={12} className="shrink-0" />
          <a href={`mailto:${supplier.email}`} className="hover:text-primary transition-colors truncate">{supplier.email}</a>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Globe size={12} className="shrink-0" />
          <a href={`https://${supplier.website}`} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
            {supplier.website}
          </a>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 mb-3">
        {supplier.products.map((p) => (
          <span key={p} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-muted text-muted-foreground border border-border">
            {p}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2 pt-3 border-t border-border">
        <div>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">Min. Order</p>
          <p className="text-xs font-bold text-foreground mt-0.5">{supplier.minOrder}</p>
        </div>
        <div>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">Lead Time</p>
          <p className="text-xs font-bold text-foreground mt-0.5">{supplier.leadTime}</p>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-1">
        {supplier.certifications.map((c) => (
          <span key={c} className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-50 text-green-700 border border-green-200">
            {c}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── ADD SUPPLIER MODAL ───────────────────────────────────────────────────────

const emptySupplier = (): Omit<ThaiSupplier, 'id'> => ({
  name: '',
  category: '',
  products: [],
  address: '',
  phone: '',
  email: '',
  website: '',
  certifications: [],
  minOrder: '',
  leadTime: '',
  rating: 4.0,
});

function AddSupplierModal({
  onAdd,
  onClose,
}: {
  onAdd: (supplier: ThaiSupplier) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState(emptySupplier());
  const [productsInput, setProductsInput] = useState('');
  const [certsInput, setCertsInput] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = () => {
    if (!form.name.trim() || !form.category.trim()) {
      setError('Company name and category are required.');
      return;
    }
    const newSupplier: ThaiSupplier = {
      ...form,
      id: 's' + Date.now(),
      products: productsInput.split(',').map((s) => s.trim()).filter(Boolean),
      certifications: certsInput.split(',').map((s) => s.trim()).filter(Boolean),
    };
    onAdd(newSupplier);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-card rounded-2xl border border-border shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-card z-10">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-green-50 flex items-center justify-center">
              <Plus size={16} className="text-green-600" />
            </div>
            <h2 className="text-base font-bold text-foreground">Add New Supplier</h2>
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Form */}
        <div className="p-5 space-y-3">
          {error && (
            <div className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-600 font-medium">
              {error}
            </div>
          )}

          <div>
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Company Name *</label>
            <input
              className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="e.g. Thai Union Group PCL"
              value={form.name}
              onChange={(e) => { setForm({ ...form, name: e.target.value }); setError(''); }}
            />
          </div>

          <div>
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Category *</label>
            <input
              className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="e.g. Seafood & Fish Products"
              value={form.category}
              onChange={(e) => { setForm({ ...form, category: e.target.value }); setError(''); }}
            />
          </div>

          <div>
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Products (comma-separated)</label>
            <input
              className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="e.g. Canned Tuna, Frozen Shrimp"
              value={productsInput}
              onChange={(e) => setProductsInput(e.target.value)}
            />
          </div>

          <div>
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Address</label>
            <input
              className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="Full address"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Phone</label>
              <input
                className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="+66 xx xxx xxxx"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Rating (0–5)</label>
              <input
                type="number"
                min={0}
                max={5}
                step={0.1}
                className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                value={form.rating}
                onChange={(e) => setForm({ ...form, rating: parseFloat(e.target.value) || 0 })}
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Email</label>
            <input
              className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="export@company.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>

          <div>
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Website</label>
            <input
              className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="www.company.com"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Min. Order</label>
              <input
                className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="USD 10,000"
                value={form.minOrder}
                onChange={(e) => setForm({ ...form, minOrder: e.target.value })}
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Lead Time</label>
              <input
                className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="30–45 days"
                value={form.leadTime}
                onChange={(e) => setForm({ ...form, leadTime: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Certifications (comma-separated)</label>
            <input
              className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="ISO 9001, HACCP, BRC"
              value={certsInput}
              onChange={(e) => setCertsInput(e.target.value)}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border sticky bottom-0 bg-card">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-border text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition-colors"
          >
            <Plus size={14} /> Add Supplier
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

type TabId = 'overview' | 'exports' | 'imports' | 'buyers' | 'suppliers';

const tabs: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: 'overview', label: 'Overview', icon: <BarChart size={15} /> },
  { id: 'exports', label: 'India → Bangkok', icon: <ArrowUpRight size={15} /> },
  { id: 'imports', label: 'Bangkok → India', icon: <ArrowDownLeft size={15} /> },
  { id: 'buyers', label: 'Active Buyers', icon: <Users size={15} /> },
  { id: 'suppliers', label: 'Credible Suppliers', icon: <Building2 size={15} /> },
];

export default function TradeDashboardPage() {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [suppliers, setSuppliers] = useState<ThaiSupplier[]>(initialThaiSuppliers);
  const [showAddModal, setShowAddModal] = useState(false);
  const [tabSearchQuery, setTabSearchQuery] = useState('');

  const handleSaveSupplier = (updated: ThaiSupplier) => {
    setSuppliers((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
  };

  const handleDeleteSupplier = (id: string) => {
    setSuppliers((prev) => prev.filter((s) => s.id !== id));
  };

  const handleAddSupplier = (newSupplier: ThaiSupplier) => {
    setSuppliers((prev) => [...prev, newSupplier]);
  };

  const handleSearchNavigate = (tab: TabId, query: string) => {
    setActiveTab(tab);
    setTabSearchQuery(query);
  };

  // Per-tab filtered data
  const sq = tabSearchQuery.toLowerCase();

  const filteredSuppliers = sq
    ? suppliers.filter(
        (s) =>
          s.name.toLowerCase().includes(sq) ||
          s.category.toLowerCase().includes(sq) ||
          s.products.some((p) => p.toLowerCase().includes(sq)) ||
          s.address.toLowerCase().includes(sq) ||
          s.email.toLowerCase().includes(sq)
      )
    : suppliers;

  const filteredBuyers = sq
    ? indianRestaurantsBangkok.filter(
        (b) =>
          b.name.toLowerCase().includes(sq) ||
          b.type.toLowerCase().includes(sq) ||
          b.phone.toLowerCase().includes(sq) ||
          b.email.toLowerCase().includes(sq) ||
          b.keyImports.some((k) => k.toLowerCase().includes(sq))
      )
    : indianRestaurantsBangkok;

  const filteredExports = sq
    ? indiaToThailandExports.filter((e) => e.category.toLowerCase().includes(sq))
    : indiaToThailandExports;

  const filteredImports = sq
    ? thailandToIndiaImports.filter((i) => i.category.toLowerCase().includes(sq))
    : thailandToIndiaImports;

  return (
    <AppLayout>
      {showAddModal && (
        <AddSupplierModal onAdd={handleAddSupplier} onClose={() => setShowAddModal(false)} />
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-2xl">🇮🇳</span>
            <span className="text-lg text-muted-foreground font-medium">↔</span>
            <span className="text-2xl">🇹🇭</span>
            <h1 className="text-2xl font-bold text-foreground tracking-tight ml-1">India – Bangkok Trade Dashboard</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Bilateral trade intelligence · FY 2023–24 data · Bangkok / Thailand market
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold border border-blue-200">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
            FY 2023–24
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-50 text-green-700 text-xs font-semibold border border-green-200">
            <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
            Live Research
          </span>
        </div>
      </div>

      {/* Unified Search Bar */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <UnifiedSearchBar suppliers={suppliers} onNavigate={handleSearchNavigate} />
        {tabSearchQuery && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              Filtering <span className="font-semibold text-foreground">"{tabSearchQuery}"</span> in {tabs.find((t) => t.id === activeTab)?.label}
            </span>
            <button
              onClick={() => setTabSearchQuery('')}
              className="flex items-center gap-1 px-2 py-1 rounded-lg bg-muted text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors border border-border"
            >
              <X size={11} /> Clear
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-muted/60 rounded-xl p-1 overflow-x-auto scrollbar-thin">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); setTabSearchQuery(''); }}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all duration-150 ${
              activeTab === tab.id
                ? 'bg-card text-primary shadow-card border border-border'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ── */}
      {activeTab === 'overview' && (
        <div className="fade-in space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="India Exports to Thailand"
              value="$7.62B"
              sub="Total FY 2023–24"
              trend={14.2}
              color="#4F6EF7"
              icon={<ArrowUpRight size={18} />}
            />
            <StatCard
              label="India Imports from Thailand"
              value="$8.05B"
              sub="Total FY 2023–24"
              trend={11.8}
              color="#F59E0B"
              icon={<ArrowDownLeft size={18} />}
            />
            <StatCard
              label="Total Bilateral Trade"
              value="$15.67B"
              sub="Combined volume"
              trend={12.9}
              color="#10B981"
              icon={<TrendingUp size={18} />}
            />
            <StatCard
              label="Active Indian Buyers (BKK)"
              value="200+"
              sub="Restaurants & food businesses"
              color="#8B5CF6"
              icon={<Users size={18} />}
            />
          </div>

          {/* Charts row */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
            {/* Export bar chart */}
            <div className="xl:col-span-2 bg-card rounded-xl border border-border shadow-card p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-sm font-bold text-foreground">Top India Exports to Thailand</h2>
                  <p className="text-xs text-muted-foreground">USD Million · FY 2023–24</p>
                </div>
                <span className="text-xs font-semibold px-2 py-1 rounded-lg bg-primary/10 text-primary">Export</span>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={exportChartData} barSize={28}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                    formatter={(v: number) => [`$${v}M`, 'Value']}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {exportChartData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Pie chart */}
            <div className="bg-card rounded-xl border border-border shadow-card p-5">
              <div className="mb-4">
                <h2 className="text-sm font-bold text-foreground">Trade Balance</h2>
                <p className="text-xs text-muted-foreground">India–Thailand bilateral split</p>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={tradePieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {tradePieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Legend
                    iconType="circle"
                    iconSize={8}
                    formatter={(value) => <span style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{value}</span>}
                  />
                  <Tooltip
                    contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                    formatter={(v: number) => [`$${v}M`, '']}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Import bar chart */}
          <div className="bg-card rounded-xl border border-border shadow-card p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-sm font-bold text-foreground">Top Thailand Exports to India (India Imports)</h2>
                <p className="text-xs text-muted-foreground">USD Million · FY 2023–24</p>
              </div>
              <span className="text-xs font-semibold px-2 py-1 rounded-lg bg-amber-50 text-amber-700 border border-amber-200">Import</span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={importChartData} barSize={28}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                  formatter={(v: number) => [`$${v}M`, 'Value']}
                />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {importChartData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── EXPORTS TAB ── */}
      {activeTab === 'exports' && (
        <div className="fade-in">
          <div className="bg-card rounded-xl border border-border shadow-card p-5 mb-5">
            <div className="flex items-center gap-3 mb-1">
              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <ArrowUpRight size={16} className="text-primary" />
              </div>
              <div>
                <h2 className="text-base font-bold text-foreground">What Bangkok (Thailand) Imports Most from India</h2>
                <p className="text-xs text-muted-foreground">Top 10 product categories by export value · FY 2023–24</p>
              </div>
            </div>
          </div>

          {filteredExports.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Search size={28} className="text-muted-foreground mb-3" />
              <p className="text-sm font-semibold text-foreground mb-1">No export products match "{tabSearchQuery}"</p>
              <button onClick={() => setTabSearchQuery('')} className="text-xs text-primary hover:underline mt-1">Clear search</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="bg-card rounded-xl border border-border shadow-card p-5">
                <h3 className="text-sm font-bold text-foreground mb-4">Ranked by Export Value</h3>
                <div className="divide-y divide-border">
                  {filteredExports.map((item, i) => (
                    <ProductRow key={item.category} item={item} rank={i + 1} />
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-card rounded-xl border border-border shadow-card p-5">
                  <h3 className="text-sm font-bold text-foreground mb-3">Export Volume Chart</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={exportChartData} layout="vertical" barSize={16}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} width={80} />
                      <Tooltip
                        contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                        formatter={(v: number) => [`$${v}M`, 'Value']}
                      />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {exportChartData.map((entry, i) => (
                          <Cell key={i} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl border border-primary/20 p-4">
                  <h3 className="text-sm font-bold text-foreground mb-2">🔑 Key Insights</h3>
                  <ul className="space-y-1.5 text-xs text-muted-foreground">
                    <li className="flex items-start gap-2"><span className="text-primary font-bold mt-0.5">→</span>Pharmaceuticals lead at $1.24B — Thailand is India's 4th largest pharma export destination in ASEAN</li>
                    <li className="flex items-start gap-2"><span className="text-primary font-bold mt-0.5">→</span>Spices & Condiments grew 22.5% YoY driven by Thai food industry demand for authentic Indian spices</li>
                    <li className="flex items-start gap-2"><span className="text-primary font-bold mt-0.5">→</span>Rice exports surged 31% — India's Basmati and non-Basmati rice are in high demand post-2023 Thai crop shortfall</li>
                    <li className="flex items-start gap-2"><span className="text-primary font-bold mt-0.5">→</span>Processed Foods up 19.6% — growing Indian diaspora and restaurant sector driving demand</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── IMPORTS TAB ── */}
      {activeTab === 'imports' && (
        <div className="fade-in">
          <div className="bg-card rounded-xl border border-border shadow-card p-5 mb-5">
            <div className="flex items-center gap-3 mb-1">
              <div className="h-8 w-8 rounded-lg bg-amber-50 flex items-center justify-center">
                <ArrowDownLeft size={16} className="text-amber-600" />
              </div>
              <div>
                <h2 className="text-base font-bold text-foreground">What India Imports from Bangkok (Thailand)</h2>
                <p className="text-xs text-muted-foreground">Top 10 product categories by import value · FY 2023–24</p>
              </div>
            </div>
          </div>

          {filteredImports.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Search size={28} className="text-muted-foreground mb-3" />
              <p className="text-sm font-semibold text-foreground mb-1">No import products match "{tabSearchQuery}"</p>
              <button onClick={() => setTabSearchQuery('')} className="text-xs text-primary hover:underline mt-1">Clear search</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="bg-card rounded-xl border border-border shadow-card p-5">
                <h3 className="text-sm font-bold text-foreground mb-4">Ranked by Import Value</h3>
                <div className="divide-y divide-border">
                  {filteredImports.map((item, i) => (
                    <ImportRow key={item.category} item={item} rank={i + 1} />
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-card rounded-xl border border-border shadow-card p-5">
                  <h3 className="text-sm font-bold text-foreground mb-3">Import Volume Chart</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={importChartData} layout="vertical" barSize={16}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} width={80} />
                      <Tooltip
                        contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                        formatter={(v: number) => [`$${v}M`, 'Value']}
                      />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {importChartData.map((entry, i) => (
                          <Cell key={i} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-xl border border-amber-200 p-4">
                  <h3 className="text-sm font-bold text-foreground mb-2">🔑 Key Insights</h3>
                  <ul className="space-y-1.5 text-xs text-muted-foreground">
                    <li className="flex items-start gap-2"><span className="text-amber-600 font-bold mt-0.5">→</span>Electronic Components dominate at $2.1B — Thailand is a global hub for hard disk drives and semiconductors</li>
                    <li className="flex items-start gap-2"><span className="text-amber-600 font-bold mt-0.5">→</span>Rubber products at $1.45B — Thailand is world's largest natural rubber producer, critical for India's auto sector</li>
                    <li className="flex items-start gap-2"><span className="text-amber-600 font-bold mt-0.5">→</span>Tropical Fruits grew 28.7% — Durian, Mangosteen, and Rambutan gaining popularity in Indian metro markets</li>
                    <li className="flex items-start gap-2"><span className="text-amber-600 font-bold mt-0.5">→</span>Cosmetics up 21.5% — Thai beauty brands expanding rapidly in India's premium skincare segment</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── BUYERS TAB ── */}
      {activeTab === 'buyers' && (
        <div className="fade-in">
          <div className="bg-card rounded-xl border border-border shadow-card p-5 mb-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-purple-50 flex items-center justify-center">
                  <Users size={16} className="text-purple-600" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-foreground">Active Indian Buyers in Bangkok</h2>
                  <p className="text-xs text-muted-foreground">Major Indian restaurants & food businesses — verified contacts</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-purple-50 text-purple-700 border border-purple-200">
                  {filteredBuyers.length} Active Buyer{filteredBuyers.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>

          {filteredBuyers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Search size={28} className="text-muted-foreground mb-3" />
              <p className="text-sm font-semibold text-foreground mb-1">No buyers match "{tabSearchQuery}"</p>
              <button onClick={() => setTabSearchQuery('')} className="text-xs text-primary hover:underline mt-1">Clear search</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredBuyers.map((buyer) => (
                <BuyerCard key={buyer.name} buyer={buyer} />
              ))}
            </div>
          )}

          <div className="mt-5 bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-xl border border-purple-200 p-4">
            <h3 className="text-sm font-bold text-foreground mb-2">📋 Procurement Notes</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-muted-foreground">
              <div className="flex items-start gap-2">
                <Package size={13} className="text-purple-600 shrink-0 mt-0.5" />
                <span>Most restaurants source Basmati rice, whole spices, ghee, and lentils directly from Indian exporters or via Bangkok-based Indian grocery distributors.</span>
              </div>
              <div className="flex items-start gap-2">
                <Truck size={13} className="text-purple-600 shrink-0 mt-0.5" />
                <span>Key distribution hubs: Pahurat (Little India), Silom area, and Sukhumvit corridor. Indian grocery stores like Moghul Supermarket act as B2B intermediaries.</span>
              </div>
              <div className="flex items-start gap-2">
                <ShoppingBag size={13} className="text-purple-600 shrink-0 mt-0.5" />
                <span>Combined monthly procurement of listed restaurants exceeds ฿1.06M (~USD 30,000). Michelin-starred venues prefer premium/organic Indian produce.</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── SUPPLIERS TAB ── */}
      {activeTab === 'suppliers' && (
        <div className="fade-in">
          <div className="bg-card rounded-xl border border-border shadow-card p-5 mb-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-green-50 flex items-center justify-center">
                  <Building2 size={16} className="text-green-600" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-foreground">Credible Thai Suppliers for Indian Importers</h2>
                  <p className="text-xs text-muted-foreground">Verified exporters from Thailand · certified & trade-ready</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-green-50 text-green-700 border border-green-200">
                  {filteredSuppliers.length} Verified Supplier{filteredSuppliers.length !== 1 ? 's' : ''}
                </span>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-semibold hover:bg-green-700 transition-colors"
                >
                  <Plus size={13} /> Add Supplier
                </button>
              </div>
            </div>
          </div>

          {filteredSuppliers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="h-14 w-14 rounded-2xl bg-muted flex items-center justify-center mb-3">
                <Search size={24} className="text-muted-foreground" />
              </div>
              <p className="text-sm font-semibold text-foreground mb-1">
                {tabSearchQuery ? `No suppliers match "${tabSearchQuery}"` : 'No suppliers yet'}
              </p>
              <p className="text-xs text-muted-foreground mb-4">
                {tabSearchQuery ? 'Try a different search term.' : 'Add your first supplier to get started.'}
              </p>
              {tabSearchQuery ? (
                <button onClick={() => setTabSearchQuery('')} className="text-xs text-primary hover:underline">Clear search</button>
              ) : (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition-colors"
                >
                  <Plus size={14} /> Add Supplier
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredSuppliers.map((supplier) => (
                <EditableSupplierCard
                  key={supplier.id}
                  supplier={supplier}
                  onSave={handleSaveSupplier}
                  onDelete={handleDeleteSupplier}
                />
              ))}
            </div>
          )}

          <div className="mt-5 bg-gradient-to-br from-green-50 to-green-100/50 rounded-xl border border-green-200 p-4">
            <h3 className="text-sm font-bold text-foreground mb-2">✅ Sourcing Tips for Indian Importers</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-muted-foreground">
              <div className="flex items-start gap-2">
                <Leaf size={13} className="text-green-600 shrink-0 mt-0.5" />
                <span>Register with DITP (Department of International Trade Promotion, Thailand) for verified supplier lists and trade fair invitations.</span>
              </div>
              <div className="flex items-start gap-2">
                <Gem size={13} className="text-green-600 shrink-0 mt-0.5" />
                <span>Use THAIFEX – Anuga Asia (annual food trade fair in Bangkok) to meet suppliers and negotiate MOQs directly.</span>
              </div>
              <div className="flex items-start gap-2">
                <Zap size={13} className="text-green-600 shrink-0 mt-0.5" />
                <span>ASEAN-India FTA (AIFTA) provides preferential tariff rates — verify HS codes with DGFT India for duty savings on Thai imports.</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
